import PageLayout from "../../components/PageLayout";
import { useResponsive } from "../../lib/useResponsive";

const allocations = [
  {
    pct: "45%",
    title: "ETFs — Diversified Foundation",
    desc: "Tech & innovation-focused ETFs providing broad, stable market exposure.",
    tickers: ["QQQ", "XLK", "DRAM", "AIPO"],
  },
  {
    pct: "50%",
    title: "Stocks — High-Conviction Growth",
    desc: "Semiconductor, AI, and next-decade growth leaders.",
    tickers: ["MU", "NVDA", "TSLA", "GOOGL", "QCOM", "NBIS"],
  },
  {
    pct: "5%",
    title: "Cash — Tactical Reserve",
    desc: "Dry powder for market dips and opportunistic entries.",
    tickers: ["Liquidity"],
  },
];

const edges = [
  { icon: "📈", title: "Buy the Dip", desc: "When others panic-sell, we deploy cash reserves & tactical margin to buy at optimal prices." },
  { icon: "🎯", title: "25+ Years Combined Experience", desc: "Expertise across capital markets, banking, financial operations & enterprise leadership." },
  { icon: "⭐", title: "Strategic Mentorship", desc: "Guided by foundational early investors who specialize in identifying transformational technologies early." },
  { icon: "🕒", title: "Daily Active Management", desc: "No set-and-forget. Allocations shift systematically with market conditions every trading day." },
];

export default function StrategyPage() {
  const { isMobile, isTablet } = useResponsive();

  return (
    <PageLayout
      eyebrow="Investment Strategy"
      title={<>Where your money <em>works</em>.</>}
      subtitle=""
    >
      {/* Allocation Stack */}
      <div style={{ ...S.allocStack, marginBottom: isMobile ? 24 : 32 }}>
        {allocations.map((a, i) => (
          <div key={i} style={{
            ...S.allocCard,
            flexDirection: isMobile ? "row" : "row",
            padding: isMobile ? "16px" : "24px 20px",
            gap: isMobile ? 16 : 24,
            alignItems: isMobile ? "flex-start" : "center",
          }}>
            <div style={{ ...S.allocPct, fontSize: isMobile ? 28 : 36, minWidth: isMobile ? 56 : 80 }}>{a.pct}</div>
            <div style={S.allocText}>
              <h4 style={{ ...S.allocTitle, fontSize: isMobile ? 14 : 16 }}>{a.title}</h4>
              <p style={{ ...S.allocDesc, fontSize: isMobile ? 13 : 14, marginBottom: isMobile ? 8 : 12 }}>{a.desc}</p>
              <div style={S.allocTickers}>
                {a.tickers.map((t) => (
                  <span key={t} style={S.ticker}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* What Sets Us Apart */}
      <div style={{ ...S.whyCard, padding: isMobile ? "16px" : 28, marginBottom: isMobile ? 16 : 24 }}>
        <h3 style={{ ...S.cardTitle, fontSize: isMobile ? 16 : 18 }}>What Sets Us Apart</h3>
        <div style={{
          ...S.edgeGrid,
          gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(auto-fit, minmax(240px, 1fr))",
          gap: isMobile ? 16 : 24,
        }}>
          {edges.map((e, i) => (
            <div key={i} style={S.edgeItem}>
              <div style={{ ...S.edgeIcon, fontSize: isMobile ? 18 : 20 }}>{e.icon}</div>
              <div style={S.edgeBody}>
                <strong style={{ fontSize: isMobile ? 13 : 14 }}>{e.title}</strong>
                <p style={{ ...S.edgeDesc, fontSize: isMobile ? 12 : 13 }}>{e.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Note */}
      <div style={{ ...S.riskNote, fontSize: isMobile ? 11 : 12 }}>
        <p>
          <strong>On leverage:</strong> Up to 30% of portfolio value may be used
          tactically to amplify gains during structural macro opportunities — managed
          with strict risk discipline.
        </p>
      </div>
    </PageLayout>
  );
}

const S: Record<string, React.CSSProperties> = {
  allocStack: { display: "flex", flexDirection: "column", gap: 16, marginBottom: 32 },
  allocCard: {
    display: "flex", gap: 24, padding: "24px 20px", alignItems: "center",
    background: "rgba(255,255,255,0.03)", borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.07)",
  },
  allocPct: { fontSize: 36, fontWeight: 800, color: "var(--teal-light)", minWidth: 80, flexShrink: 0 },
  allocText: { flex: 1, minWidth: 0 },
  allocTitle: { fontSize: 16, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 },
  allocDesc: { fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 12 },
  allocTickers: { display: "flex", gap: 6, flexWrap: "wrap" },
  ticker: {
    padding: "3px 10px", borderRadius: 6, fontSize: 11,
    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
    color: "var(--text-primary)", fontWeight: 600, letterSpacing: "0.05em",
  },
  whyCard: {
    background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 12, padding: 28, marginBottom: 24,
  },
  cardTitle: { fontSize: 18, fontWeight: 600, color: "var(--text-primary)", marginBottom: 20 },
  edgeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24 },
  edgeItem: { display: "flex", gap: 14, alignItems: "flex-start" },
  edgeIcon: { fontSize: 20, minWidth: 24, flexShrink: 0 },
  edgeBody: { fontSize: 14, color: "var(--text-primary)", lineHeight: 1.5 },
  edgeDesc: { fontSize: 13, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.5 },
  riskNote: { fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6, fontStyle: "italic" },
};
